/**
 * Environment Redirect Utility
 * Handles redirecting users from staging to production when needed
 */

/**
 * Check if current environment should redirect to production
 * @returns {boolean} - true if redirect is needed
 */
export function shouldRedirectToProduction() {
  // Get environment variables
  const redirectEnabled = import.meta.env.VITE_REDIRECT_TO_PRODUCTION === 'true';
  const currentUrl = window.location.href;
  const isStaging = currentUrl.includes('staging.igwprints.com');
  
  console.log('[EnvironmentRedirect] Checking redirect conditions:', {
    redirectEnabled,
    isStaging,
    currentUrl
  });
  
  return redirectEnabled && isStaging;
}

/**
 * Get the production URL to redirect to
 * @returns {string} - production URL
 */
export function getProductionUrl() {
  const productionUrl = import.meta.env.VITE_PRODUCTION_URL || 'https://sales.igwprints.com';
  const currentPath = window.location.pathname;
  const currentSearch = window.location.search;
  
  // Preserve the current path and query parameters
  return `${productionUrl}${currentPath}${currentSearch}`;
}

/**
 * Show redirect message to user
 * @param {string} message - custom message or default
 * @returns {Promise} - resolves when user is ready for redirect
 */
export function showRedirectMessage(message = '') {
  const defaultMessage = import.meta.env.VITE_REDIRECT_MESSAGE || 
    'This staging environment has been merged to production. Redirecting you to the live system...';
  
  const displayMessage = message || defaultMessage;
  
  return new Promise((resolve) => {
    // Create a simple modal-like overlay
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
      font-family: system-ui, -apple-system, sans-serif;
    `;
    
    const modal = document.createElement('div');
    modal.style.cssText = `
      background: white;
      padding: 2rem;
      border-radius: 8px;
      max-width: 500px;
      text-align: center;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    `;
    
    modal.innerHTML = `
      <div style="margin-bottom: 1rem;">
        <svg style="width: 48px; height: 48px; color: #3B82F6; margin: 0 auto;" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clip-rule="evenodd" />
        </svg>
      </div>
      <h3 style="font-size: 1.25rem; font-weight: 600; margin-bottom: 1rem; color: #1F2937;">
        Redirecting to Production
      </h3>
      <p style="color: #6B7280; margin-bottom: 1.5rem;">
        ${displayMessage}
      </p>
      <div style="display: flex; gap: 1rem; justify-content: center;">
        <button id="redirect-now" style="
          background: #3B82F6;
          color: white;
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
        ">
          Go Now
        </button>
        <button id="stay-here" style="
          background: #6B7280;
          color: white;
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
        ">
          Stay Here
        </button>
      </div>
      <div id="countdown" style="margin-top: 1rem; color: #6B7280; font-size: 0.875rem;"></div>
    `;
    
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    
    // Handle button clicks
    const redirectBtn = modal.querySelector('#redirect-now');
    const stayBtn = modal.querySelector('#stay-here');
    const countdownEl = modal.querySelector('#countdown');
    
    redirectBtn.addEventListener('click', () => {
      document.body.removeChild(overlay);
      resolve(true);
    });
    
    stayBtn.addEventListener('click', () => {
      document.body.removeChild(overlay);
      resolve(false);
    });
    
    // Auto-redirect countdown
    const delay = parseInt(import.meta.env.VITE_REDIRECT_DELAY) || 3000;
    let timeLeft = Math.ceil(delay / 1000);
    
    const updateCountdown = () => {
      if (timeLeft > 0) {
        countdownEl.textContent = `Auto-redirecting in ${timeLeft} seconds...`;
        timeLeft--;
        setTimeout(updateCountdown, 1000);
      } else {
        document.body.removeChild(overlay);
        resolve(true);
      }
    };
    
    updateCountdown();
  });
}

/**
 * Perform the redirect to production
 * @param {string} customUrl - optional custom URL to redirect to
 */
export function redirectToProduction(customUrl = '') {
  const targetUrl = customUrl || getProductionUrl();
  
  console.log('[EnvironmentRedirect] Redirecting to production:', targetUrl);
  
  // Store redirect information in sessionStorage for potential debugging
  sessionStorage.setItem('igw_redirected_from_staging', JSON.stringify({
    timestamp: new Date().toISOString(),
    fromUrl: window.location.href,
    toUrl: targetUrl
  }));
  
  // Perform the redirect
  window.location.replace(targetUrl);
}

/**
 * Main function to handle environment redirect logic
 * Called from the main application entry point
 */
export async function handleEnvironmentRedirect() {
  try {
    if (shouldRedirectToProduction()) {
      console.log('[EnvironmentRedirect] Redirect conditions met, showing message...');
      
      const shouldRedirect = await showRedirectMessage();
      
      if (shouldRedirect) {
        redirectToProduction();
      } else {
        console.log('[EnvironmentRedirect] User chose to stay on staging');
        // Optionally, you could set a session flag to not show this again
        sessionStorage.setItem('igw_staging_redirect_dismissed', 'true');
      }
    }
  } catch (error) {
    console.error('[EnvironmentRedirect] Error during redirect handling:', error);
  }
}

/**
 * Check if user has dismissed the redirect for this session
 * @returns {boolean}
 */
export function hasUserDismissedRedirect() {
  return sessionStorage.getItem('igw_staging_redirect_dismissed') === 'true';
}

/**
 * Reset the redirect dismissal (useful for testing)
 */
export function resetRedirectDismissal() {
  sessionStorage.removeItem('igw_staging_redirect_dismissed');
} 